import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { formatReference } from '@skjv/common';
import {
  BookmarkIcon,
  NoteIcon,
  CopyIcon,
  CheckIcon,
  ShareIcon,
  MemoryIcon,
  // ChatIcon,
  SparklesIcon,
  StudyIcon,
} from '@components/icons';
import { VerseMenuAnalysisRow } from './verse-menu-analysis-row';
import { isDesktop } from '@/desktop/desktop';

/**
 * Verse action menu - appears when clicking a verse number.
 * Provides options to bookmark, add notes, copy, share, etc.
 */
export const VerseMenu = observer(function VerseMenu() {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    'exiting',
  );
  const [copySuccess, setCopySuccess] = useState(false);

  const isVisible = bibleState.verseMenuVisible;
  const location = bibleState.interactionLocation;

  // Safety net for the reader-click prefetch (idempotent)
  useEffect(() => {
    if (isVisible && location) bibleState.loadVerseAnalysisVerdict(location);
  }, [isVisible, location]);

  // Handle visibility changes with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), 200);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => {
        setShouldRender(false);
        setCopySuccess(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Handle outside click to close
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.verse-menu-panel')) {
        bibleState.hideVerseMenu();
      }
    };

    // Delay adding listener to avoid immediate close from the verse click
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isVisible]);

  // Get verse text for copy/share
  const getVerseText = useCallback(async (): Promise<string> => {
    if (!location) return '';

    // Find the verse element in the DOM
    const verseId = `v-${location.testamentIndex}-${location.bookIndex}-${location.chapterIndex}-${location.verseIndex}`;
    const verseEl = document.getElementById(verseId);
    if (!verseEl) return '';

    // Walk child nodes to capture words, spaces, and punctuation
    let text = '';
    verseEl.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (!el.classList.contains('verse-num')) {
          text += el.textContent || '';
        }
      }
    });

    return text.replace(/\s+/g, ' ').trim();
  }, [location]);

  // Handle copy
  const handleCopy = useCallback(async () => {
    if (!location) return;

    const reference = formatReference(location);
    const verseText = await getVerseText();
    const fullText = `${reference} - ${verseText}`;

    try {
      await navigator.clipboard.writeText(fullText);
      setCopySuccess(true);
      setTimeout(() => {
        bibleState.hideVerseMenu();
      }, 800);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [location, getVerseText]);

  // Handle share
  const handleShare = useCallback(async () => {
    if (!location) return;

    const reference = formatReference(location);
    const verseText = await getVerseText();
    const fullText = `${reference} - ${verseText}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: reference,
          text: fullText,
        });
        bibleState.hideVerseMenu();
      } catch {
        // User cancelled or error - ignore
      }
    } else {
      // Fallback to copy
      handleCopy();
    }
  }, [location, getVerseText, handleCopy]);

  // Handle bookmark
  const handleBookmark = useCallback(() => {
    bibleState.showBookmarkCreator();
  }, []);

  // Handle note
  const handleNote = useCallback(() => {
    bibleState.showNoteEditor();
  }, []);

  // Handle "It is written..." — opens chat panel with deterministic verse study
  const handleItIsWritten = useCallback(() => {
    if (!location) return;
    const ref = formatReference(location);
    bibleState.addLocationToHistory(location);
    bibleState.showChatPanel();
    bibleState.addVerseStudy(ref);
    bibleState.hideVerseMenu();
  }, [location]);

  // TODO: re-enable when AI chat is ready
  // const handleAskAbout = useCallback(() => {
  //   if (!location) return;
  //   const ref = formatReference(location);
  //   bibleState.showChatPanel();
  //   bibleState.sendChatMessage(`What does ${ref} mean?`);
  //   bibleState.hideVerseMenu();
  // }, [location]);

  // Handle memory verse
  const handleMemoryVerse = useCallback(() => {
    if (location) {
      bibleState.setMemoryVerse(location);
      bibleState.hideVerseMenu();
    }
  }, [location]);

  // Handle start study — open study panel with a new study primed at this verse
  const handleStartStudy = useCallback(() => {
    if (!location) return;
    bibleState.showStudyPanel();
    bibleState.startCreateStudy();
    bibleState.updateEditingStudy({ startingLocation: location });
    bibleState.hideVerseMenu();
  }, [location]);

  if (!shouldRender || !location) return null;

  const reference = formatReference(location);

  return (
    <div className={`verse-menu-overlay ${animationState}`}>
      <div className="verse-menu-panel">
        {/* Header */}
        <div className="verse-menu-header">
          <span className="verse-menu-reference">{reference}</span>
        </div>

        {/* Menu Items */}
        <div className="verse-menu-items">
          <button className="verse-menu-item verse-menu-item-cta" onClick={handleItIsWritten}>
            <SparklesIcon className="h-5 w-5" />
            <span>It is written...</span>
          </button>

          {/* Bookmarks + notes are native-only (DESKTOP-PRD §3.8) */}
          {isDesktop() && (
            <button className="verse-menu-item" onClick={handleBookmark}>
              <BookmarkIcon className="h-5 w-5" />
              <span>Set Bookmark</span>
            </button>
          )}

          {isDesktop() && (
            <button className="verse-menu-item" onClick={handleNote}>
              <NoteIcon className="h-5 w-5" />
              <span>Create Note</span>
            </button>
          )}

          <div className="verse-menu-divider" />

          <button className="verse-menu-item" onClick={handleCopy}>
            {copySuccess ? (
              <>
                <CheckIcon className="h-5 w-5 text-green-600" />
                <span className="text-green-600">Copied!</span>
              </>
            ) : (
              <>
                <CopyIcon className="h-5 w-5" />
                <span>Copy</span>
              </>
            )}
          </button>

          {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
            <button className="verse-menu-item" onClick={handleShare}>
              <ShareIcon className="h-5 w-5" />
              <span>Share</span>
            </button>
          )}

          {/* TODO: re-enable when AI chat is ready
          <button className="verse-menu-item" onClick={handleAskAbout}>
            <ChatIcon className="h-5 w-5" />
            <span>Ask About</span>
          </button>
          */}

          <div className="verse-menu-divider" />

          <button className="verse-menu-item" onClick={handleMemoryVerse}>
            <MemoryIcon className="h-5 w-5" />
            <span>Set Memory Verse</span>
          </button>

          {isDesktop() && (
            <button className="verse-menu-item" onClick={handleStartStudy}>
              <StudyIcon className="h-5 w-5" />
              <span>Start Study</span>
            </button>
          )}

          {/* KJV vs NASB analysis — a peer row at the foot, not a headline */}
          <VerseMenuAnalysisRow location={location} />
        </div>
      </div>
    </div>
  );
});

export default VerseMenu;
